import React, { useEffect, useMemo } from 'react'
import { Box } from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useFeatureGate, FEATURE_KEYS } from '@/context/SubscriptionContext'
import { EmptyState, Skeleton } from '@/design-system'
import { useAlbumDetail } from '@/features/album/hooks/useAlbumDetail'
import { withGalleryId } from '@/features/album/utils/albumXData'
import { useUpdateTopic } from '@/hooks/api/dashboard'
import { WizardShell } from '../components/WizardShell'
import { IntentStep } from '../components/IntentStep'
import { ScenePicker } from '../components/ScenePicker'
import { FramePicker } from '../components/FramePicker'
import { ColorPickerStep } from '../components/ColorPickerStep'
import { MaterialPicker } from '../components/MaterialPicker'
import { PrintSlotBoard } from '../components/PrintSlotBoard'
import { MattingControls } from '../components/MattingControls'
import { ReviewSummary } from '../components/ReviewSummary'
import { RoomPreview } from '@/design-system'
import { useWizardState } from '../hooks/useWizardState'
import { useCreateGalleryFromAlbum } from '../hooks/useCreateGalleryFromAlbum'
import { useWizardPreviewData } from '../hooks/useWizardPreviewData'
import { FRAME_NONE, WIZARD_STEPS, type WizardStepId } from '../types'

function parseStep(raw: string | null): WizardStepId | undefined {
  if (!raw) return undefined
  return WIZARD_STEPS.includes(raw as WizardStepId) ? (raw as WizardStepId) : undefined
}

export function GalleryWizardScreen() {
  const { albumId = '' } = useParams()
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const { isFeatureAvailable, showUpgrade } = useFeatureGate()

  useEffect(() => {
    if (!isFeatureAvailable(FEATURE_KEYS.GALLERY_3D)) {
      showUpgrade({ featureKey: FEATURE_KEYS.GALLERY_3D })
      navigate(-1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { album, loading, error } = useAlbumDetail(albumId)
  const photos = useMemo(() => album?.photos || [], [album?.photos])
  const initialStep = parseStep(search.get('step'))

  const wizard = useWizardState(albumId, photos, album?.title || 'Album', initialStep)
  const { save, saving, phase } = useCreateGalleryFromAlbum()
  const linkGalleryToAlbum = useUpdateTopic(albumId)

  // Sync step → URL for resume
  useEffect(() => {
    const next = new URLSearchParams(search)
    if (next.get('step') !== wizard.step) {
      next.set('step', wizard.step)
      setSearch(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard.step])

  // Esc closes wizard (matches album immersive) — ignore while saving
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) navigate(`/albums/${albumId}`)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [albumId, navigate, saving])

  const coverUrl = useMemo(
    () => photos[0]?.thumbUrl || photos[0]?.url || album?.coverUrl,
    [album?.coverUrl, photos],
  )

  const previewData = useWizardPreviewData(wizard.draft, coverUrl)

  const close = () => navigate(`/albums/${albumId}`)

  const onContinue = async () => {
    if (wizard.step === 'review') {
      const draft = {
        ...wizard.draft,
        frameId:
          wizard.draft.frameId === FRAME_NONE || wizard.draft.frameId == null
            ? undefined
            : wizard.draft.frameId,
      }
      const result = await save(draft)
      if (result) {
        wizard.clearDraft()
        // So the owner's album view and the guest walk-in link can find this
        // gallery without an authenticated cross-reference (see albumXData.ts).
        if (album) {
          try {
            await linkGalleryToAlbum.mutateAsync({
              topicName: album.title,
              topicXData: withGalleryId(album.topicXData, result.galleryId),
            })
          } catch {
            /* non-critical — album view falls back to the wizard entry point */
          }
        }
        // Studio's RestorePattern reads seeded notes — same path as guest copyDraftToTopic
        navigate(`/galleries/${result.galleryId}/studio?fromAlbum=1`)
      }
      return
    }
    wizard.goNext()
  }

  if (loading && !album) {
    return (
      <Box
        className='ds-canvas'
        minHeight='100dvh'
        px={5}
        pt={5}
        maxWidth={960}
        mx='auto'
        boxSizing='border-box'
      >
        <Skeleton height={20} width={160} sx={{ mb: 1.5 }} />
        <Skeleton height={3} width='100%' sx={{ mb: 5 }} rounded='pill' />
        <Skeleton height={200} rounded='lg' sx={{ mb: 3 }} />
        <Skeleton height={28} width='60%' sx={{ mb: 1.5 }} />
        <Skeleton height={16} width='80%' />
      </Box>
    )
  }

  if (error || !album) {
    return (
      <Box className='ds-canvas' minHeight='100dvh' p={5} display='flex' alignItems='center'>
        <EmptyState
          title='Album not found'
          description={error || 'Open an album first, then turn it into 3D.'}
          actionLabel='Back to albums'
          onAction={() => navigate('/albums')}
        />
      </Box>
    )
  }

  if (photos.length === 0) {
    return (
      <Box className='ds-canvas' minHeight='100dvh' p={5} display='flex' alignItems='center'>
        <EmptyState
          title='Add photos first'
          description='Upload a few photos to this album, then come back to build a 3D Gallery.'
          actionLabel='Back to album'
          onAction={close}
        />
      </Box>
    )
  }

  const continueLabel =
    wizard.step === 'review'
      ? phase === 'seeding'
        ? 'Placing photos…'
        : phase === 'creating'
          ? 'Creating…'
          : 'Create gallery'
      : 'Continue'

  return (
    <WizardShell
      step={wizard.step}
      stepIndex={wizard.stepIndex}
      canContinue={wizard.canContinue}
      continueLabel={continueLabel}
      saving={saving}
      onBack={wizard.stepIndex === 0 ? close : wizard.goBack}
      onContinue={onContinue}
      onClose={close}
      onStepSelect={wizard.goTo}
      preview={
        <RoomPreview
          sceneThumbUrl={previewData.sceneThumbUrl}
          sceneName={previewData.sceneName}
          wallTint={previewData.wallTint}
          photoUrl={previewData.photoUrl}
          matColor={previewData.matColor}
          matWidthPct={previewData.matWidthPct}
          chips={previewData.chips}
        />
      }
    >
      {wizard.step === 'intent' && (
        <IntentStep albumTitle={album.title} photoCount={photos.length} coverUrl={coverUrl} />
      )}
      {wizard.step === 'scene' && (
        <ScenePicker value={wizard.sceneId} onChange={wizard.setSceneId} />
      )}
      {wizard.step === 'frame' && (
        <FramePicker value={wizard.frameId ?? FRAME_NONE} onChange={wizard.setFrameId} />
      )}
      {wizard.step === 'color' && (
        <ColorPickerStep value={wizard.colorId} onChange={(id) => wizard.setColorId(id)} />
      )}
      {wizard.step === 'material' && (
        <MaterialPicker value={wizard.materialId} onChange={wizard.setMaterialId} />
      )}
      {wizard.step === 'prints' && (
        <PrintSlotBoard
          slots={wizard.slots}
          onToggleExclude={wizard.toggleSlotExcluded}
          onMoveLeft={(i) => wizard.moveSlot(i, i - 1)}
          onMoveRight={(i) => wizard.moveSlot(i, i + 1)}
        />
      )}
      {wizard.step === 'matting' && (
        <MattingControls
          width={wizard.matWidth}
          color={wizard.matColor}
          onWidthChange={wizard.setMatWidth}
          onColorChange={wizard.setMatColor}
        />
      )}
      {wizard.step === 'review' && (
        <ReviewSummary
          draft={wizard.draft}
          onTitleChange={wizard.setGalleryTitle}
          onPrivacyChange={wizard.setPrivacy}
          onEditStep={wizard.goTo}
        />
      )}
    </WizardShell>
  )
}
